// HSE Web Push subscription helper.
//
// Idempotent, one-shot per session: registers /sw.js, asks the
// browser for Notification permission (only if the user hasn't
// already granted or denied), subscribes to the org's VAPID key,
// and POSTs the subscription JSON to the API so it can push us
// later. Safe to call multiple times — every call short-circuits
// when we're already subscribed.
//
// Returns a discriminated result the caller can use to show a
// friendly toast + status pill if it wants; the app also fires
// the subscription in the background from the auth flow so most
// users never see anything.

import { baseUrl } from './api'
import { useAuthStore } from '../store/authStore'

export type PushEnableResult =
  | { ok: true;  state: 'subscribed' }
  | { ok: false; reason: 'unsupported' | 'permission-denied' | 'no-vapid' | 'error'; detail?: string }

function urlBase64ToUint8Array(base64: string): Uint8Array {
  // Convert VAPID base64url → Uint8Array, which is what
  // `pushManager.subscribe` expects for `applicationServerKey`.
  const padding = '='.repeat((4 - (base64.length % 4)) % 4)
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/')
  const bin = atob(b64)
  const out = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i)
  return out
}

function withAuth(headers: HeadersInit = {}): HeadersInit {
  const token = useAuthStore.getState().accessToken
  return token
    ? { ...headers, Authorization: `Bearer ${token}` }
    : headers
}

/** Register the SW + subscribe + POST the subscription. Idempotent. */
export async function enableWebPush(): Promise<PushEnableResult> {
  if (typeof window === 'undefined') {
    return { ok: false, reason: 'unsupported', detail: 'not a browser' }
  }
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    return { ok: false, reason: 'unsupported', detail: 'browser lacks Push API' }
  }
  if (!('Notification' in window)) {
    return { ok: false, reason: 'unsupported', detail: 'browser lacks Notification API' }
  }

  // Fetch VAPID key first — if the server isn't configured, bail
  // before we even prompt the user for permission.
  let publicKey: string | null = null
  try {
    const res = await fetch(`${baseUrl}/api/v1/web-push/vapid-public-key`)
    if (!res.ok) return { ok: false, reason: 'no-vapid', detail: `HTTP ${res.status}` }
    const body = await res.json()
    publicKey = body.publicKey ?? null
  } catch (e) {
    return { ok: false, reason: 'no-vapid', detail: (e as Error).message }
  }
  if (!publicKey) return { ok: false, reason: 'no-vapid' }

  // Register the SW at the origin root so its scope covers the
  // whole app. Safe to call every time — the browser returns the
  // existing registration if one already exists.
  const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' })
  await navigator.serviceWorker.ready

  // Permission prompt. Chrome / Firefox only surface it once per
  // origin; subsequent calls short-circuit to whatever the user
  // picked previously.
  if (Notification.permission === 'default') {
    const perm = await Notification.requestPermission()
    if (perm !== 'granted') return { ok: false, reason: 'permission-denied' }
  } else if (Notification.permission === 'denied') {
    return { ok: false, reason: 'permission-denied' }
  }

  // Subscribe. Re-use the existing subscription if the browser
  // already has one for this origin + VAPID key.
  let sub = await reg.pushManager.getSubscription()
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      // `applicationServerKey` accepts a BufferSource — the Uint8Array
      // we built is one, but TS types the return type widely for
      // SharedArrayBuffer support. Explicitly pass the .buffer so
      // it matches the ArrayBufferLike constraint the DOM lib picks.
      applicationServerKey: urlBase64ToUint8Array(publicKey).buffer as ArrayBuffer,
    })
  }

  // POST the subscription so the backend can push us later.
  try {
    await fetch(`${baseUrl}/api/v1/web-push/subscribe`, {
      method: 'POST',
      headers: withAuth({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(sub.toJSON()),
    })
  } catch (e) {
    return { ok: false, reason: 'error', detail: (e as Error).message }
  }
  return { ok: true, state: 'subscribed' }
}

/** Best-effort unsubscribe. Called from sign-out so we stop
 *  pushing the current browser once the user's session is gone. */
export async function disableWebPush(): Promise<void> {
  if (!('serviceWorker' in navigator)) return
  try {
    const reg = await navigator.serviceWorker.getRegistration('/')
    if (!reg) return
    const sub = await reg.pushManager.getSubscription()
    if (!sub) return
    // Tell the server first so the row disappears; if that fails,
    // still unsubscribe locally so a signed-out browser doesn't
    // keep buzzing.
    try {
      await fetch(`${baseUrl}/api/v1/web-push/subscribe`, {
        method: 'DELETE',
        headers: withAuth({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ endpoint: sub.endpoint }),
      })
    } catch { /* fall through */ }
    await sub.unsubscribe()
  } catch { /* ignore */ }
}
